import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  EventEmitter,
  OnDestroy,
  AfterViewInit,
} from '@angular/core';

import { EMPTY, Observable, Subscription, combineLatest, filter, map, of } from 'rxjs';

import { isNotEmpty, unsubscribe } from '@fcom/utils';
import { finShare } from '@fcom/rx';

import { ScrollHandleContainerComponent } from '../scroll-handle-container/scroll-handle-container.component';
import { Direction, HistogramBar } from '../../interfaces';

@Component({
  selector: 'fin-histogram',
  templateUrl: './histogram.component.html',
  styleUrls: ['./histogram.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HistogramComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input()
  barData$: Observable<HistogramBar[]> = EMPTY;

  @Input()
  selected$ = of(0);

  @Output()
  barSelected: EventEmitter<HistogramBar> = new EventEmitter();

  @Output()
  barsScrolled: EventEmitter<Direction> = new EventEmitter();

  range$: Observable<HistogramBar[]>;
  minAmount$: Observable<number>;

  private readonly MIN_COLUMN_HEIGHT = 44;
  private readonly MAX_COLUMN_HEIGHT = 72;
  private maxAmount$: Observable<number>;

  @ViewChild('scrollContainer', { static: false }) scrollContainer: ScrollHandleContainerComponent;
  @ViewChildren('bar', { read: ElementRef }) bars: QueryList<ElementRef>;

  subscription: Subscription = new Subscription();

  ngOnInit(): void {
    this.minAmount$ = this.barData$.pipe(
      map(
        (barData: HistogramBar[]) =>
          barData &&
          Math.min(
            ...barData
              .filter((bar: HistogramBar) => +bar.amount && +bar.amount > 0)
              .map((bar: HistogramBar) => +bar.amount)
          )
      ),
      finShare()
    );

    this.maxAmount$ = this.barData$.pipe(
      map(
        (barData: HistogramBar[]) =>
          barData &&
          Math.max(
            ...barData
              .filter((bar: HistogramBar) => +bar.amount && +bar.amount > 0)
              .map((bar: HistogramBar) => +bar.amount)
          )
      ),
      finShare()
    );

    this.range$ = combineLatest([
      this.barData$.pipe(filter((barData) => isNotEmpty(barData))),
      this.minAmount$,
      this.maxAmount$,
    ]).pipe(
      map(([barData, minAmount, maxAmount]: [HistogramBar[], number, number]) =>
        barData.map((bar: HistogramBar) => ({
          ...bar,
          height: `${this.calculateHeightBar(bar, minAmount, maxAmount)}px`,
          isCheapest: +bar.amount === minAmount && !bar.noFlight,
        }))
      ),
      finShare()
    );
  }

  ngAfterViewInit(): void {
    if (this.scrollContainer) {
      this.scrollContainer.updateScrollHandles();
    }

    this.subscription.add(
      this.selected$.subscribe((value: number) => {
        if (this.scrollContainer) {
          this.scrollContainer.scrollToElement(this.bars.get(value));
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription = unsubscribe(this.subscription);
  }

  /**
   * Displays bar center based on container
   * @param index, bar index to display centered
   */
  scrollToBarByIndex(selectedBar: HistogramBar): void {
    this.scrollContainer.scrollToElement(this.bars.get(selectedBar.index));
    this.barSelected.emit(selectedBar);
  }

  private calculateHeightBar(bar: HistogramBar, minAmount: number, maxAmount: number): number {
    if (minAmount === maxAmount) {
      return this.MIN_COLUMN_HEIGHT;
    }

    if (+bar.amount === maxAmount) {
      return this.MAX_COLUMN_HEIGHT;
    }

    const percentOfAmount = ((+bar.amount - minAmount) / (maxAmount - minAmount)) * 100;
    const extraHeightInPx = ((this.MAX_COLUMN_HEIGHT - this.MIN_COLUMN_HEIGHT) * percentOfAmount) / 100;

    return this.MIN_COLUMN_HEIGHT + extraHeightInPx;
  }
}
